import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AutoManagedCommissionInfluencers.graphql';
import { Query, QueryautoManagedCommissionInfluencersArgs } from '../../__generated__/globalTypes';

export const useAutoManagedCommissionInfluencersQuery = (
  options?: QueryHookOptions<Query, QueryautoManagedCommissionInfluencersArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryautoManagedCommissionInfluencersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAutoManagedCommissionInfluencersLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryautoManagedCommissionInfluencersArgs>
): QueryResult<Query, QueryautoManagedCommissionInfluencersArgs> & {
  getAutoManagedCommissionInfluencers: LazyQueryExecFunction<Query, QueryautoManagedCommissionInfluencersArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAutoManagedCommissionInfluencers, result] = useLazyQuery<Query, QueryautoManagedCommissionInfluencersArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAutoManagedCommissionInfluencers, ...result };
};

export const useAutoManagedCommissionInfluencersPromiseQuery = () => {
  const client = useApolloClient();

  const getAutoManagedCommissionInfluencers = useCallback(
    (options?: Omit<QueryOptions<QueryautoManagedCommissionInfluencersArgs, Query>, 'query'>) =>
      client.query<Query, QueryautoManagedCommissionInfluencersArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAutoManagedCommissionInfluencers };
};
// Suspense Query Hooks
export const useAutoManagedCommissionInfluencersSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryautoManagedCommissionInfluencersArgs>
) => {
  const result = useSuspenseQuery<Query, QueryautoManagedCommissionInfluencersArgs>(QUERY, {
    ...options
  });

  return result;
};
